import { useState } from "react";
import type { LandingContent, Locale, PageKind } from "../content/types";
import { localeHref } from "../i18n/routes";
import { Container } from "./Container";
import { BrandMark } from "./BrandMark";
import { Icon } from "./Icon";
import { LanguageSwitch } from "./LanguageSwitch";
import { MobileMenu } from "./MobileMenu";

type HeaderProps = {
  locale: Locale;
  kind: PageKind;
  nav: LandingContent["nav"];
  variant?: "full" | "minimal";
};

export function Header({ locale, kind, nav, variant = "full" }: HeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const landingHref = localeHref(locale, "landing");
  const anchor = (hash: string) => (kind === "landing" ? `#${hash}` : `${landingHref}#${hash}`);

  const navLinks = [
    { label: nav.features, href: anchor("features") },
    { label: nav.privacy, href: localeHref(locale, "privacy") },
    { label: nav.support, href: localeHref(locale, "support") }
  ];

  return (
    <header className="sticky top-0 z-50 border-b border-subtle bg-[#0B1018E6] backdrop-blur-md">
      <Container>
        <div className="flex h-[68px] items-center justify-between">
          <a href={landingHref} className="flex items-center gap-2.5" aria-label="Luxoria home">
            <BrandMark size={30} />
            <span className="font-display text-lg font-bold text-text-primary">Luxoria</span>
          </a>

          {variant === "full" ? (
            <nav className="hidden items-center gap-8 md:flex" aria-label="Primary">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="text-sm font-medium text-text-secondary transition-colors hover:text-text-primary"
                >
                  {link.label}
                </a>
              ))}
            </nav>
          ) : null}

          <div className="flex items-center gap-3">
            <LanguageSwitch locale={locale} kind={kind} label={nav.language} />

            {variant === "full" ? (
              <button
                type="button"
                className="inline-flex h-9 w-9 items-center justify-center rounded-[10px] border border-subtle text-text-primary md:hidden"
                aria-label={nav.menu}
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen(true)}
              >
                <Icon name="menu" size={18} />
              </button>
            ) : null}
          </div>
        </div>
      </Container>

      {variant === "full" ? (
        <MobileMenu locale={locale} nav={nav} open={menuOpen} onClose={() => setMenuOpen(false)} />
      ) : null}
    </header>
  );
}
